import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, Phone, MapPin, Thermometer, Sun, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

// Photo réelle d'une installation fournie par le client (façade, effet miroir, lumineuse).
const HERO_IMAGE = "/images/hero-facade.webp";
const pillIcons = [Thermometer, Sun, EyeOff];

export default function HeroScrollReveal() {
  const { t } = useLanguage();
  const pills = t("hero.pills");

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-slate-950">
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Façade d'une résidence avec pellicules réfléchissantes installées par Reflectum Pellicule, effet miroir de jour"
          width={1500}
          height={2000}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/25" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 lg:py-44 min-h-[85vh] flex items-center">
        <div className="max-w-3xl">
          {/* Pastilles */}
          <div data-testid="hero-pills" className="flex flex-wrap gap-2.5">
            {pills.map((p, i) => {
              const PillIcon = pillIcons[i] || Sun;
              return (
                <span
                  key={p}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium text-[#E2D3BE] border border-[#C5A880]/40 bg-white/5 backdrop-blur-md"
                >
                  <PillIcon className="w-3.5 h-3.5 text-[#C5A880]" />
                  {p}
                </span>
              );
            })}
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-[2.6rem] sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] text-white mt-7"
          >
            {t("hero.title1")}
            <br />
            <span className="text-[#C5A880]">{t("hero.title2")}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg lg:text-xl text-slate-200 max-w-2xl leading-relaxed">
            {t("hero.subtitle")}
          </p>

          {/* Note Google */}
          <div data-testid="hero-rating" className="mt-7 flex items-center gap-2.5">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-4 h-4 text-[#EAB308] fill-[#EAB308]" />
              ))}
            </div>
            <span className="text-sm font-medium text-slate-200">{t("hero.rating")}</span>
          </div>

          {/* CTA */}
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-cta-primary">
              <Button className="w-full sm:w-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-8 py-3.5 text-base font-medium shadow-lg shadow-black/20 group whitespace-normal text-center leading-snug">
                {t("hero.cta")}
                <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <a href="tel:+14382556438" data-testid="hero-cta-call">
              <Button
                variant="outline"
                className="w-full sm:w-auto rounded-full border-[#C5A880]/50 bg-white/5 backdrop-blur-md text-[#E2D3BE] hover:bg-white/10 hover:text-white h-auto min-h-14 px-8 py-3.5 text-base font-medium"
              >
                <Phone className="mr-2 w-4 h-4" />
                {t("hero.callCta")}
              </Button>
            </a>
          </div>
          <p data-testid="hero-cta-reassurance" className="mt-4 text-sm text-slate-300">
            {t("hero.reassurance")}
          </p>
          <p data-testid="hero-satisfaction" className="mt-2.5 inline-flex items-center gap-2 text-sm font-medium text-[#E2D3BE]">
            <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
            {t("hero.satisfaction")}
          </p>

          {/* Zones desservies */}
          <div data-testid="hero-areas" className="mt-8 inline-flex items-center gap-2 text-sm text-slate-300">
            <MapPin className="w-4 h-4 text-[#C5A880]" />
            {t("hero.areas")}
          </div>
        </div>
      </div>
    </section>
  );
}
